import { Routes } from '@angular/router';

import { authGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';

import { LoginComponent } from './features/auth/login/login.component';
import { AMENITIES_ROUTES } from './features/catalogs/amenities/amenities.routes';
import { CHARGERS_ROUTES } from './features/catalogs/chargers/chargers.routes';
import { COMMERCIAL_CONDITIONS_ROUTES } from './features/catalogs/commercial-conditions/commercial-conditions.routes';
import { COMMERCIAL_ZONES_ROUTES } from './features/catalogs/commercial-zones/commercial-zones.routes';
import { COMPANIES_ROUTES } from './features/catalogs/companies/companies.routes';
import { CONNECTOR_TYPES_ROUTES } from './features/catalogs/connector-types/connector-types.routes';
import { CONNECTORS_ROUTES } from './features/catalogs/connectors/connectors.routes';
import { CUSTOMER_GROUPS_ROUTES } from './features/catalogs/customer-groups/customer-groups.routes';
import { CUSTOMERS_ROUTES } from './features/catalogs/customers/customers.routes';
import { FAQS_ROUTES } from './features/catalogs/faqs/faqs.routes';
import { POSTAL_CODES_ROUTES } from './features/catalogs/postal-codes/postal-codes.routes';
import { POWER_RANGES_ROUTES } from './features/catalogs/power-ranges/power-ranges.routes';
import { REGIONS_ROUTES } from './features/catalogs/regions/regions.routes';
import { ROLES_ROUTES } from './features/catalogs/roles/roles.routes';
import { STATIONS_ROUTES } from './features/catalogs/stations/stations.routes';
import { USERS_ROUTES } from './features/catalogs/users/users.routes';
import { VEHICLE_MODELS_ROUTES } from './features/catalogs/vehicle-models/vehicle-models.routes';
import { ZONES_ROUTES } from './features/catalogs/zones/zones.routes';
import { HomeComponent } from './features/home/home.component';
import { PRICES_ROUTES } from './features/management/prices/prices.routes';
import { PUSH_NOTIFICATIONS_ROUTES } from './features/management/push-notifications/push-notifications.routes';
import { MISSING_PRICE_CONFIGURATIONS_REPORT_ROUTES } from './features/reports/missing-price-configurations-report/missing-price-configurations-report.routes';

import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';

/**
 * Rutas de la aplicación (se usa HashLocation: las URLs quedan como `/#/ruta`).
 *
 * - Públicas: login, 2FA, páginas legales y eliminación de cuenta.
 * - Autenticadas: todo lo que cuelga de `MainLayoutComponent`, protegido por `authGuard`
 *   (sesión) y `permissionGuard` (permiso del módulo según la URL).
 *
 * Cada módulo define sus rutas en `<módulo>.routes.ts` (componentes con carga diferida).
 * Al agregar un módulo nuevo, registrar su segmento en `PROTECTED_ROUTE_SEGMENTS`
 * de `permission.guard.ts`, su título en el mapa de `topbar.component.ts` y su entrada
 * de menú (con su permiso) en `sidebar.component.ts`.
 */
export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: '2FA',
    loadComponent: () =>
      import('./features/auth/two-factor-auth/two-factor-auth.component').then(
        (m) => m.TwoFactorAuthComponent,
      ),
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [permissionGuard],
    children: [
      { path: '', component: HomeComponent },

      ...AMENITIES_ROUTES,
      ...CHARGERS_ROUTES,
      ...COMMERCIAL_CONDITIONS_ROUTES,
      ...COMMERCIAL_ZONES_ROUTES,
      ...COMPANIES_ROUTES,
      ...CONNECTORS_ROUTES,
      ...CONNECTOR_TYPES_ROUTES,
      ...CUSTOMERS_ROUTES,
      ...CUSTOMER_GROUPS_ROUTES,
      ...FAQS_ROUTES,
      ...MISSING_PRICE_CONFIGURATIONS_REPORT_ROUTES,
      ...POSTAL_CODES_ROUTES,
      ...POWER_RANGES_ROUTES,
      ...PRICES_ROUTES,
      ...PUSH_NOTIFICATIONS_ROUTES,
      ...REGIONS_ROUTES,
      ...ROLES_ROUTES,
      ...STATIONS_ROUTES,
      ...USERS_ROUTES,
      ...VEHICLE_MODELS_ROUTES,
      ...ZONES_ROUTES,

      {
        path: 'stations-map',
        loadComponent: () =>
          import(
            './features/management/stations/stations-map/stations-map.component'
          ).then((m) => m.StationsMapComponent),
      },
      {
        path: 'station-reviews',
        loadComponent: () =>
          import(
            './features/management/stations/station-reviews/station-reviews-list/station-reviews-list.component'
          ).then((m) => m.StationReviewsListComponent),
      },
      {
        path: 'charging-transactions',
        loadComponent: () =>
          import(
            './features/management/charging-transactions/charging-transactions-list/charging-transactions-list.component'
          ).then((m) => m.ChargingTransactionsListComponent),
      },
      {
        path: 'reservation-policies',
        loadComponent: () =>
          import(
            './features/management/reservation-policies/reservation-policies-list/reservation-policies-list.component'
          ).then((m) => m.ReservationPoliciesListComponent),
      },
      {
        path: 'loyalty-programs',
        loadComponent: () =>
          import(
            './features/management/loyalty-programs/loyalty-programs-list/loyalty-programs-list.component'
          ).then((m) => m.LoyaltyProgramsListComponent),
      },
      {
        path: 'yena-products',
        loadComponent: () =>
          import(
            './features/management/yena-products/yena-products-list/yena-products-list.component'
          ).then((m) => m.YenaProductsListComponent),
      },
      {
        path: 'invoices',
        loadComponent: () =>
          import(
            './features/management/invoices/invoices-list/invoices-list.component'
          ).then((m) => m.InvoicesListComponent),
      },
      {
        path: 'charger-notifications',
        loadComponent: () =>
          import(
            './features/management/charger-notifications/charger-notifications-list/charger-notifications-list.component'
          ).then((m) => m.ChargerNotificationsListComponent),
      },
    ],
  },
  {
    path: 'legal',
    loadChildren: () => import('./pages/legal/legal.routes'),
  },
  {
    path: 'account-deletion',
    loadComponent: () =>
      import('./pages/account.deletion/account.deletion').then(
        (m) => m.AccountDeletion,
      ),
  },
  {
    path: '**',
    redirectTo: '',
  },
];
